import { AuthProvider } from '@firebase/auth';
import { FacebookAuthProvider, GoogleAuthProvider, OAuthProvider } from '@firebase/auth';
import {FirebaseAuthProviderId} from "./auth-providers";

/**
 * Firebase Authentication error codes
 */
export const FirebaseErrorCode = {
  CREDENTIAL_ALREADY_IN_USE: 'auth/credential-already-in-use',
  EMAIL_ALREADY_IN_USE: 'auth/email-already-in-use',
  ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL: 'auth/account-exists-with-different-credential',
  WRONG_PASSWORD: 'auth/wrong-password',
  INVALID_CREDENTIAL: 'auth/invalid-credential',
  INVALID_EMAIL: 'auth/invalid-email',
  WEAK_PASSWORD: 'auth/weak-password',
  OPERATION_NOT_ALLOWED: 'auth/operation-not-allowed',
  REQUIRES_RECENT_LOGIN: 'auth/requires-recent-login',
  POPUP_CLOSED_BY_USER: 'auth/popup-closed-by-user',
  CANCELLED_POPUP_REQUEST: 'auth/cancelled-popup-request',
  POPUP_BLOCKED: 'auth/popup-blocked',
} as const;

export function buildProvider(providerId: string): AuthProvider {
  switch (providerId) {
    case FirebaseAuthProviderId.GOOGLE:
      return new GoogleAuthProvider();
    case FirebaseAuthProviderId.FACEBOOK:
      return new FacebookAuthProvider();
    case FirebaseAuthProviderId.APPLE:
      return new OAuthProvider(FirebaseAuthProviderId.APPLE);
    default:
      throw new Error(`Unsupported provider: ${providerId}`);
  }
}

export function mapFirebaseError(e: any): string {
  const code: string | undefined = e?.code;
  switch (code) {
    case FirebaseErrorCode.CREDENTIAL_ALREADY_IN_USE:
    case FirebaseErrorCode.EMAIL_ALREADY_IN_USE:
      return $localize`:@@common.validation.emailInUse:This email address is already in use by another account.`;
    case FirebaseErrorCode.ACCOUNT_EXISTS_WITH_DIFFERENT_CREDENTIAL:
      return $localize`:@@common.validation.accountExistsWithDifferentCredential:There is already an account with the same email address, but a different log-in method.`;
    case FirebaseErrorCode.WRONG_PASSWORD:
      return $localize`:@@common.validation.wrongPassword:The password you entered is incorrect. Please try again.`;
    case FirebaseErrorCode.INVALID_CREDENTIAL:
      return $localize`:@@common.validation.invalidCredential:The credentials provided are invalid. Please try again.`;
    case FirebaseErrorCode.INVALID_EMAIL:
      return $localize`:@@common.validation.invalidEmail:This email address is not valid.`;
    case FirebaseErrorCode.WEAK_PASSWORD:
      return $localize`:@@firebase.validation.weakPassword:Your password is too weak. Try a stronger one.`;
    case FirebaseErrorCode.OPERATION_NOT_ALLOWED:
      return $localize`:@@common.validation.operationNotAllowed:This operation is not permitted.`;
    case FirebaseErrorCode.REQUIRES_RECENT_LOGIN:
      return $localize`:@@common.msg.reauthenticate:Please log out and log back in, then try again.`;
    case FirebaseErrorCode.POPUP_CLOSED_BY_USER:
      return $localize`:@@firebase.validation.popupClosed:The authentication popup was closed. Please try again.`;
    case FirebaseErrorCode.CANCELLED_POPUP_REQUEST:
      return $localize`:@@firebase.validation.popupCancelled:The authentication request was canceled.`;
    case FirebaseErrorCode.POPUP_BLOCKED:
      return $localize`:@@firebase.validation.popupBlocked:The authentication popup was blocked by your browser. Please allow popups for this site.`;
    default:
      return $localize`:@@common.msg.unexpectedError:Unexpected error. Please try again.`;
  }
}
